import { useState, useCallback, useRef } from 'react';
import { Loader2, ChevronDown, ChevronUp, ShieldCheck, ShieldAlert, ShieldQuestion, CheckCircle2, AlertCircle, HelpCircle } from 'lucide-react';
import QueryBar, { isZoeChipIntroOnboarded } from '../components/investigator/QueryBar';
import AnswerPanel from '../components/investigator/AnswerPanel';
import TimelinePanel from '../components/investigator/TimelinePanel';
import EvidencePanel from '../components/investigator/EvidencePanel';
import FollowUps from '../components/investigator/FollowUps';
import EmptyState from '../components/investigator/EmptyState';
import ErrorState from '../components/investigator/ErrorState';
import PresentationRenderer, { KpiTiles, BadgeGroup, Callout, EnrichedTimeline, MilestoneTimeline } from '../components/investigator/presentation/PresentationRenderer';
import TraceDebugPanel, { isDebugMode } from '../components/investigator/TraceDebugPanel';
import { streamInvestigation } from '../services/investigatorApi';
import type { InvestigationResponse, InvestigationProgressEvent, InvestigationStep, PresentationBlock, KpiGroupBlock, BadgeGroupBlock, CalloutBlock, TimelineBlock, MilestoneTimelineBlock, ConfidenceLevel } from '../types/investigator';

const CONFIDENCE_PILL: Record<ConfidenceLevel, { label: string; cls: string; icon: typeof ShieldCheck }> = {
  high:   { label: 'High Confidence',   cls: 'bg-success/40 border-primary text-success',  icon: ShieldCheck },
  medium: { label: 'Medium Confidence', cls: 'bg-warning/40 border-warning text-warning',  icon: ShieldAlert },
  low:    { label: 'Low Confidence',    cls: 'bg-destructive/40   border-destructive   text-destructive',    icon: ShieldQuestion },
};

type PageState = 'empty' | 'loading' | 'success' | 'error';

/** Pick blocks of a given type from the presentation payload. */
function blocksOfType<T extends PresentationBlock>(
  blocks: PresentationBlock[] | undefined,
  type: PresentationBlock['type'],
): T[] {
  return (blocks?.filter((b) => b.type === type) ?? []) as T[];
}

/** Map a tool name to a compact human label for the step log. */
function toolLabel(tool: string): string {
  const MAP: Record<string, string> = {
    resolve_vehicle: 'Resolving vehicle identity',
    resolve_order: 'Resolving order identity',
    resolve_driver: 'Resolving driver identity',
    get_vehicle_timeline: 'Fetching vehicle timeline',
    get_order_evidence: 'Fetching order evidence',
    get_driver_compliance: 'Fetching driver compliance',
    get_operational_anomalies: 'Scanning for operational anomalies',
    get_data_reliability: 'Assessing data reliability',
    get_entity_aliases: 'Looking up entity aliases',
    get_entity_events: 'Fetching entity events',
    query_graph_neighbours: 'Querying graph relationships',
  };
  return MAP[tool] ?? tool.replace(/_/g, ' ');
}

export default function Investigator() {
  const [state, setState] = useState<PageState>('empty');
  const [result, setResult] = useState<InvestigationResponse | null>(null);
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [steps, setSteps] = useState<InvestigationStep[]>([]);
  /**
   * T29 — the thread this page is in. Previously every question started a new
   * conversation, so a follow-up (including answering a clarification) reached
   * the planner with no memory of what was asked or offered.
   */
  const conversationIdRef = useRef<string | undefined>(undefined);
  const lastQuestion = useRef('');
  const isSubmittingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const stepCounterRef = useRef(0);

  const addStep = useCallback((step: Omit<InvestigationStep, 'id'>) => {
    const id = ++stepCounterRef.current;
    setSteps((prev) => [...prev, { id, ...step }]);
    return id;
  }, []);


  const handleProgress = useCallback((event: InvestigationProgressEvent) => {
    if (event.type === 'thinking') {
      addStep({ status: 'running', label: event.message });
    } else if (event.type === 'tool_call') {
      const label = toolLabel(event.tool);
      const detail = event.args_preview ? `(${event.args_preview})` : undefined;
      addStep({ status: 'running', label, detail });
    } else if (event.type === 'tool_result') {
      // Mark the most recent running step for this tool as done.
      setSteps((prev) => {
        const lastRunning = [...prev].reverse().find(
          (s) => s.status === 'running' && s.label === toolLabel(event.tool)
        );
        if (!lastRunning) return prev;
        const resultDetail = event.claims > 0
          ? `${event.claims} claim${event.claims !== 1 ? 's' : ''} found`
          : event.limitations > 0
          ? 'No data'
          : 'Complete';
        return prev.map((s) => s.id === lastRunning.id
          ? { ...s, status: 'done', detail: resultDetail }
          : s
        );
      });
    } else if (event.type === 'plan') {
      const firstTool = event.plan.proposed_tools[0]?.name ?? '—';
      addStep({
        status: 'done',
        label: 'Plan ready',
        detail: `${event.plan.question_type} · first tool: ${toolLabel(firstTool)}`,
      });
    } else if (event.type === 'window_validated') {
      const range = `${event.window.from} → ${event.window.to}`;
      const suffix = event.adjusted
        ? ` (clamped${event.adjustment_reason ? ': ' + event.adjustment_reason : ''})`
        : '';
      addStep({
        status: 'done',
        label: 'Window validated',
        detail: `${range}${suffix}`,
      });
    } else if (event.type === 'reflection') {
      const gaps = event.result.gaps.length;
      const detail = event.result.state === 'sufficient'
        ? `round ${event.round} — sufficient`
        : `round ${event.round} — ${event.result.state}${gaps ? `, ${gaps} gap${gaps !== 1 ? 's' : ''}` : ''}`;
      addStep({ status: 'done', label: 'Reflection', detail });
    } else if (event.type === 'verification') {
      const m = event.result.metrics;
      const detail = event.result.ok
        ? `OK · ${m.claim_count} claim${m.claim_count !== 1 ? 's' : ''}, ${m.distinct_capabilities_called} tool${m.distinct_capabilities_called !== 1 ? 's' : ''}`
        : event.result.issues[0] ?? 'issue detected';
      addStep({
        status: event.result.ok ? 'done' : 'error',
        label: 'Verified',
        detail,
      });
    } else if (event.type === 'complete' || event.type === 'bound_plan_complete') {
      // Mark any still-running thinking steps as done.
      setSteps((prev) => prev.map((s) => s.status === 'running' ? { ...s, status: 'done' } : s));
    } else if (event.type === 'narrating') {
      addStep({ status: 'running', label: 'Composing answer' });
    } else if (event.type === 'bound_plan_understanding') {
      const detail = event.question_understanding.trim().slice(0, 120) || undefined;
      addStep({ status: 'done', label: 'Understood the question', detail });
    } else if (event.type === 'bound_plan_created') {
      addStep({
        status: 'done',
        label: 'Plan ready',
        detail: `${event.steps.length} step${event.steps.length !== 1 ? 's' : ''}`,
      });
    } else if (event.type === 'bound_plan_step_started') {
      addStep({
        status: 'running',
        label: toolLabel(event.tool),
        detail: event.args_preview ? `(${event.args_preview})` : undefined,
        stepKey: `bp-${event.step}`,
      });
    } else if (event.type === 'bound_plan_step_finished') {
      // Bound steps run in parallel waves — match on the stable step key first.
      const key = `bp-${event.step}`;
      const tool = event.tool;
      const status: InvestigationStep['status'] = event.status === 'error' ? 'error' : 'done';
      const detail =
        event.status === 'ok'
          ? (event.claims ?? 0) > 0
            ? `${event.claims} claim${event.claims !== 1 ? 's' : ''} found`
            : (event.limitations ?? 0) > 0
            ? 'No data'
            : 'Complete'
          : event.status === 'error'
          ? event.error || 'Step failed'
          : event.skipped_reason || 'Skipped';
      setSteps((prev) => {
        const match = [...prev].reverse().find(
          (s) => s.status === 'running' && (s.stepKey === key || s.label === toolLabel(tool)),
        );
        if (!match) return prev;
        return prev.map((s) => (s.id === match.id ? { ...s, status, detail } : s));
      });
    }
  }, [addStep]);

  const handleSubmit = useCallback(async (
    question: string,
    clarificationResponse?: { clarification_id: string; option_id: string },
  ) => {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || isSubmittingRef.current) return;

    // Cancel any in-flight request.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    isSubmittingRef.current = true;
    lastQuestion.current = trimmedQuestion;
    setState('loading');
    setIsExpanded(false);
    setError('');
    setSteps([]);
    stepCounterRef.current = 0;
    try {
      const data = await streamInvestigation(
        {
          question: trimmedQuestion,
          ...(conversationIdRef.current ? { conversationId: conversationIdRef.current } : {}),
          ...(clarificationResponse ? { clarificationResponse } : {}),
        },
        handleProgress,
        controller.signal,
      );
      if (data.conversation_id) conversationIdRef.current = data.conversation_id;
      setResult(data);
      setState('success');
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return; // cancelled by new submission
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setState('error');
    } finally {
      isSubmittingRef.current = false;
    }
  }, [handleProgress]);

  /**
   * T29 — answer a clarification by picking one of its readings.
   *
   * Submits the option's own label as the next question. That keeps the
   * reading the user chose in their own words rather than as an opaque "2",
   * so the question log reads correctly, and the planner receives a
   * self-contained question instead of something it has to re-interpret. The
   * original question stays visible to it through the conversation context.
   */
  const handleClarificationChoice = useCallback(
    (clarificationId: string, option: { id: string; label: string }) => {
      // The label goes in as the question so the thread and the question log
      // read as what the user meant rather than as "2". The ids alongside it
      // are what actually binds this turn to the clarification: the server
      // re-reads the chosen wording from its own record, so a stale or
      // tampered label cannot put words into the planner's context.
      void handleSubmit(option.label, {
        clarification_id: clarificationId,
        option_id: option.id,
      });
    },
    [handleSubmit],
  );

  const handleRetry = useCallback(() => {
    if (!lastQuestion.current || isSubmittingRef.current) return;
    void handleSubmit(lastQuestion.current);
  }, [handleSubmit]);

  const isLoading = state === 'loading';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-foreground">Investigator</h1>
        <p className="text-sm text-muted-foreground">Query the truth layer — orders, vehicles, drivers, anomalies, evidence</p>
      </div>

      {/* Query Bar */}
      <QueryBar onSubmit={handleSubmit} isLoading={isLoading} zoeChipIntroOnboarded={isZoeChipIntroOnboarded()} />

      {/* Main content area */}
      {state === 'empty' && <EmptyState />}

      {state === 'loading' && (
        <div className="flex flex-col items-start min-h-100 gap-4 pt-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 text-success animate-spin flex-shrink-0" />
            <span>Investigating…</span>
          </div>
          {steps.length > 0 && (
            <ol className="space-y-1.5 w-full max-w-lg">
              {steps.map((step) => (
                <li key={step.id} className="flex items-start gap-2 text-sm">
                  {step.status === 'running' && (
                    <Loader2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-success animate-spin" />
                  )}
                  {step.status === 'done' && (
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary" />
                  )}
                  {step.status === 'error' && (
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-destructive" />
                  )}
                  <span className="text-muted-foreground">
                    {step.label}
                    {step.detail && (
                      <span className="ml-1.5 text-muted-foreground">{step.detail}</span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {state === 'error' && (
        <ErrorState message={error} onRetry={handleRetry} isLoading={isLoading} />
      )}

      {/* ── T29: the run stopped to ask which question was meant ──────────────
          Shown INSTEAD of the answer, not beside it: there is no answer, and
          a clarification turn carries no claims by construction. */}
      {state === 'success' && result?.status === 'needs_clarification' && result.clarification && (
        <div className="bg-card/40 rounded-xl border border-warning/40 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <HelpCircle className="w-4 h-4 text-warning mt-0.5 shrink-0" />
            <p className="text-sm text-foreground">{result.clarification.question}</p>
          </div>

          <div className="space-y-2">
            {(result.clarification.options ?? []).map((opt) => (
              <button
                key={opt.id}
                onClick={() => handleClarificationChoice(result.clarification!.clarification_id, opt)}
                disabled={isLoading}
                className="w-full text-left px-3 py-2.5 rounded-lg border border-accent bg-background/40 hover:border-warning hover:bg-background/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors group"
              >
                <span className="text-sm text-foreground group-hover:text-white">{opt.label}</span>
                {opt.detail && (
                  <span className="block text-xs text-muted-foreground mt-0.5">{opt.detail}</span>
                )}
              </button>
            ))}
          </div>

          {result.clarification.free_text_allowed && (
            <p className="text-xs text-muted-foreground">
              Not one of these? Just ask again in your own words.
            </p>
          )}
        </div>
      )}

      {/* Falls through to the normal answer when the status says clarification
          but none arrived — a blank page is the one outcome worse than an
          imperfect answer. */}
      {state === 'success' && result && !(result.status === 'needs_clarification' && result.clarification) && (
        <>
          {/* ── Presentation strip: badges + confidence + callouts + expand toggle ── */}
          {(() => {
            const badgeBlocks = blocksOfType<BadgeGroupBlock>(result.presentation?.blocks, 'badge_group');
            const calloutBlocks = blocksOfType<CalloutBlock>(result.presentation?.blocks, 'callout');
            const confPill = CONFIDENCE_PILL[result.confidence.level];
            const ConfIcon = confPill.icon;
            // Show confidence in the strip only when it's meaningful signal or low
            const showConf = result.confidence.level !== 'low' || !result.presentation?.narrative?.llm_generated;

            return (
              <div className="bg-card/40 rounded-xl border border-border/50 p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Badge pills from presentation blocks */}
                  {badgeBlocks.map((b, i) => <BadgeGroup key={i} block={b} />)}

                  {/* Confidence pill */}
                  {showConf && (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${confPill.cls}`}>
                      <ConfIcon className="w-3 h-3" />
                      {confPill.label}
                    </span>
                  )}

                  {/* Callouts (e.g. missing data warnings) */}
                  {calloutBlocks.map((b, i) => <Callout key={i} block={b} />)}

                  {/* Expand/collapse toggle */}
                  <button
                    onClick={() => setIsExpanded((v) => !v)}
                    className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-lg border border-accent text-muted-foreground hover:text-foreground hover:border-muted-foreground text-xs transition-colors"
                  >
                    {isExpanded ? (
                      <><ChevronUp className="w-3.5 h-3.5" /> Collapse</>
                    ) : (
                      <><ChevronDown className="w-3.5 h-3.5" /> Show detail</>
                    )}
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ── KPI tiles (always visible when present) ───────────────────────── */}
          {(() => {
            const kpiBlocks = blocksOfType<KpiGroupBlock>(result.presentation?.blocks, 'kpi_group');
            return kpiBlocks.length > 0 ? (
              <div className="bg-card/40 rounded-xl border border-border/50 p-4">
                {kpiBlocks.map((b, i) => <KpiTiles key={i} block={b} />)}
              </div>
            ) : null;
          })()}

          {/* ── Collapsible detail: three-panel layout ────────────────────────── */}
          {isExpanded && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ minHeight: '500px' }}>
              {/* Answer panel — narrative headline + paragraphs */}
              <div className="bg-card/40 rounded-xl border border-border/50 p-4">
                <AnswerPanel data={result} />
              </div>

              {/* Timeline: enriched when presentation block available, legacy fallback */}
              <div className="bg-card/40 rounded-xl border border-border/50 p-4">
                {(() => {
                  // Prefer the full lifecycle when one was gathered: it is a
                  // superset of the flat timeline (it shows the steps that did
                  // NOT happen too), so showing both would put two disagreeing
                  // timelines of the same order side by side.
                  const milestoneBlocks = blocksOfType<MilestoneTimelineBlock>(
                    result.presentation?.blocks,
                    'milestone_timeline',
                  );
                  if (milestoneBlocks.length > 0) {
                    return <MilestoneTimeline block={milestoneBlocks[0]} />;
                  }
                  const timelineBlocks = blocksOfType<TimelineBlock>(result.presentation?.blocks, 'timeline');
                  return timelineBlocks.length > 0
                    ? <EnrichedTimeline block={timelineBlocks[0]} />
                    : <TimelinePanel evidence={result.evidence} />;
                })()}
              </div>

              {/* Evidence panel */}
              <div className="bg-card/40 rounded-xl border border-border/50 p-4">
                <EvidencePanel evidence={result.evidence} />
              </div>
            </div>
          )}

          {/* ── Claims table (if present in presentation blocks, collapsible) ─── */}
          {isExpanded && result.presentation && blocksOfType(result.presentation.blocks, 'table').length > 0 && (
            <div className="bg-card/40 rounded-xl border border-border/50 p-4">
              <PresentationRenderer
                blocks={blocksOfType(result.presentation.blocks, 'table')}
              />
            </div>
          )}

          {/* Follow-ups */}
          <FollowUps
            followUps={result.follow_ups}
            onSelect={handleSubmit}
            isLoading={isLoading}
          />

          {/* Debug-only trace panel — shown when URL has ?debug=1 */}
          {isDebugMode() && result.metadata?.request_id && (
            <TraceDebugPanel runId={result.metadata.request_id} />
          )}
        </>
      )}
    </div>
  );
}
